/**
 * Contract for the read-only canonical-object endpoint — the data seam behind
 * the universal object peek (`?peek=<uid>`).
 *
 * The peek is a CLIENT surface that can open over any page, so it can't call
 * `loadObject` the way `/o/<uid>` does (a server component). The no-`fetch()`
 * rail means it reaches the same loader through this contract via `useApi` —
 * one endpoint, every kind. The route is a thin projection of
 * `loadObject(workspaceId, uid)` (excellent/src/modes/object-data.ts), which
 * already normalizes commitments AND the domain-table fallback kinds into one
 * shape, so the peek and the full page read the SAME resolved object and can
 * never drift into two answers for "what is this thing".
 *
 * Wire discipline: every value here is already display-ready — the actor uid is
 * resolved to a friendly name, the event type is a friendly verb, the state is
 * the stored lifecycle token (the block maps it to an intent via the shared
 * `commitmentStateIntent`, so the palette stays in one place). No raw SQLite
 * date is composed here; `formatDate`/`formatRelativeTime` run in the block.
 * `ObjectKeyFact.value` is a `ReactNode` in the block API but a plain string on
 * the wire — the loader only ever produces strings for vitals.
 */

import { z } from "zod";
import type { Operation } from "../types";

/** A resolved owner — a friendly name plus whether it's a person or an agent. */
export const objectOwnerSchema = z.object({
  name: z.string(),
  kind: z.enum(["human", "agent"]),
});

/** One kind-specific vital ("Stage", "Value", "Due") for the property rail. */
export const objectVitalSchema = z.object({
  label: z.string(),
  value: z.string(),
  hint: z.string().nullable().optional(),
  intent: z
    .enum(["neutral", "info", "success", "warning", "danger"])
    .nullable()
    .optional(),
});

/** One entity_links graph slice — "People", "Org", "Tags", … */
export const objectRelationshipGroupSchema = z.object({
  label: z.string(),
  items: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      href: z.string().nullable().optional(),
    }),
  ),
});

/** An available transition / navigational action for this object. */
export const objectActionSchema = z.object({
  key: z.string(),
  label: z.string(),
  href: z.string(),
  variant: z.enum(["default", "outline", "ghost"]).nullable().optional(),
});

/** One history row, already humanized (friendly verb + resolved actor). */
export const objectEventSchema = z.object({
  uid: z.string(),
  /** Friendly verb ("Moved to review") — never the raw catalog type. */
  label: z.string(),
  summary: z.string().nullable(),
  actorLabel: z.string().nullable(),
  occurredAt: z.string(),
  critical: z.boolean(),
});

/**
 * ── Editable field descriptors ──────────────────────────────────────────────
 *
 * Per-kind editing expressed as DATA, not components. The read leg produces an
 * `ObjectField[]`; ONE kind-free control renders each into an editor. Adding an
 * editable kind is a registry entry (`buildObjectFields`), never a `<Kind>Peek`.
 * This is the wire form of the shapes proven at `/styleguide/editable-peek`
 * (`_types.ts`); those shapes are what this contract now makes real.
 */

/** One choice in a `select`/`state`/`reference` field. */
export const objectFieldOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  /** Set only by `state` fields — the lifecycle dot colour, no per-kind map. */
  intent: z
    .enum(["neutral", "info", "success", "warning", "danger"])
    .optional(),
  /** Non-null ⇒ the item renders dimmed + unpickable, with this reason beside it. */
  lockedReason: z.string().nullable().optional(),
  /** Non-null ⇒ picking writes only WITH one line of text; this string is the prompt. */
  noteLabel: z.string().nullable().optional(),
});

/** Presentation + units for a numeric value (money today). The unit travels here,
 *  never by key-name sniffing — `scale` = powers of ten from stored int to major. */
export const objectFieldFormatSchema = z.object({
  kind: z.literal("money"),
  currency: z.string(),
  scale: z.number(),
});

/** The editable field vocabulary — `BriefFieldType` plus date/state/reference. */
export const objectFieldTypeSchema = z.enum([
  "text",
  "longtext",
  "number",
  "url",
  "select",
  "multi",
  "date",
  "state",
  "reference",
]);

/** ONE editable (or explicitly locked) property of an object. */
export const objectFieldSchema = z.object({
  /** The stored key — the ADDRESS a write is sent to, NOT a promise of a column. */
  key: z.string(),
  label: z.string(),
  type: objectFieldTypeSchema,
  /** The RAW stored value, never a display projection (a `<Select>` seeds from `4`). */
  value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
  /** The column shape, stated rather than sniffed. Required for nullable non-strings. */
  valueType: z.enum(["string", "number", "boolean"]).optional(),
  format: objectFieldFormatSchema.nullable().optional(),
  /** Resolved display label for a value the raw token can't speak for. */
  valueLabel: z.string().nullable(),
  options: z.array(objectFieldOptionSchema).nullable(),
  href: z.string().nullable(),
  /** WHY this field cannot be written — an honest locked row beats a lying control. */
  lockedReason: z.string().nullable(),
  placeholder: z.string().nullable(),
  /** "This descriptor IS the object's name." At most one per kind. */
  identity: z.boolean().optional(),
  /**
   * "This descriptor IS the object's target outcome" — the one declarative
   * sentence saying what is true when the commitment is met. At most one per
   * kind. Flagged rather than key-sniffed, exactly like `identity`: the peek
   * must not care that this kind happens to call the column `outcome`.
   *
   * Two effects, both about rendering rather than behaviour: it renders in the
   * peek's OUTCOME section (a sentence belongs in prose, never crammed into an
   * inline property chip), and it is skipped when picking the description body,
   * so a kind can carry a working description AND a stated outcome without one
   * silently eating the other's slot.
   */
  outcome: z.boolean().optional(),
});

export type ObjectFieldOption = z.infer<typeof objectFieldOptionSchema>;
export type ObjectFieldFormat = z.infer<typeof objectFieldFormatSchema>;
export type ObjectFieldType = z.infer<typeof objectFieldTypeSchema>;
export type ObjectField = z.infer<typeof objectFieldSchema>;

export const objectPeekSchema = z.object({
  uid: z.string(),
  /**
   * The kind's LOCAL integer id, when it has a domain table (task → `tasks.id`).
   * The uid is the identity; this exists only to bridge to the client seams that
   * still take an id — `tasks.get`, the `?task=<id>` deep link — so a
   * uid-addressed peek can hand off to a kind's existing rich representation.
   * Null for kinds with no domain row.
   */
  localId: z.number().nullable(),
  /** Canonical kind token ("task" | "goal" | "customer" | "person" | …). */
  kind: z.string(),
  title: z.string(),
  /** Stored lifecycle token; the block maps it to an intent + title-cases it. */
  state: z.string(),
  /** One-line "what is this" essence. */
  essence: z.string().nullable(),
  description: z.string().nullable(),
  /**
   * The commitment's "what does done look like" statement (a goal's outcome, a
   * deal's target). Distinct from both the title and the vitals — for a goal it
   * is the most important prose on the object, so the peek gives it its own
   * labeled section rather than folding it into Properties.
   */
  targetOutcome: z.string().nullable(),
  owner: objectOwnerSchema.nullable(),
  vitals: z.array(objectVitalSchema),
  relationships: z.array(objectRelationshipGroupSchema),
  actions: z.array(objectActionSchema),
  /** The parent commitment this object projects from (§6.5.4). */
  fulfills: z
    .object({ label: z.string(), href: z.string() })
    .nullable(),
  /**
   * The CONTAINER this object was created inside — the project a task belongs
   * to, the account a lead sits under — rendered as the masthead breadcrumb
   * ("Apple → Lead") with `label` linking to `href`.
   *
   * NOT `fulfills`, and the two must not be collapsed: `fulfills` is a
   * COMMITMENT edge (a task projects from the goal that spawned it) and is the
   * object's reason for existing; this is a CONTAINMENT edge and is the object's
   * address. A task routinely has both — it lives in a project AND advances a
   * goal — so one field could only ever hold one of the two answers.
   *
   * Optional, and absent for any kind with no container clause in `loadParent`
   * — the breadcrumb collapses to the bare kind noun, which is what every kind
   * renders today.
   */
  parent: z
    .object({ label: z.string(), href: z.string() })
    .nullable()
    .optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  fulfilledAt: z.string().nullable(),
  /** Newest-first, capped by the route; `eventTotal` carries the full count. */
  events: z.array(objectEventSchema),
  eventTotal: z.number(),
  /**
   * The canonical full-page destination (`/o/<uid>`), composed server-side so
   * the peek's Expand target and the loader agree by construction.
   */
  href: z.string(),
  /**
   * How this kind is removed, or null when it cannot be. Resolved server-side
   * from the ONE universal soft-delete registry (`ARCHIVABLE`), so the peek's
   * destructive control exists for every archivable kind and for no other —
   * rather than being a button one kind's bespoke peek happened to draw.
   *
   * `verb` is the registry's own word: containers say "Archive", objects say
   * "Delete". `label` is the kind's noun ("issue", "deal") for the confirm copy.
   */
  remove: z
    .object({ verb: z.enum(["Delete", "Archive"]), label: z.string() })
    .nullable()
    .optional(),
  /**
   * The kind's editable (or explicitly locked) property surface, produced by the
   * read leg. Optional so every existing caller and the current route stay valid
   * — absent (the client reads `fields ?? []`) renders exactly as today.
   */
  fields: z.array(objectFieldSchema).optional(),
});

export type ObjectPeekDto = z.infer<typeof objectPeekSchema>;

export const objectsGet: Operation<{ uid: string }, void, void, ObjectPeekDto> = {
  method: "GET",
  path: ({ uid }) => `/api/objects/${uid}`,
  params: z.object({ uid: z.string().min(1) }),
  response: objectPeekSchema,
};

/**
 * The body of ONE field write — RO-2-06's conversion of the `writeObjectField`
 * server action (`excellent/src/modes/object-write-actions.ts`).
 *
 * THREE keys and no more, and that bound is the whole safety argument. The
 * editable-peek PRD (`docs/design/EDITABLE-OBJECT-PEEK-PRD.md`, §Write & safety
 * model rule 1) refuses a generic `objects.update` by name: what it refuses is a
 * CLIENT-SUPPLIED BAG OF COLUMNS addressed at a uid. This shape cannot express
 * one. `key` is an ADDRESS that the kind's own registry entry (`KIND_EDITORS`,
 * `object-editors.ts`) resolves to an EXISTING write seam — it is not a column
 * name and carries no promise that a column exists — and there is no way to name
 * a table, a column, or a second key in the same request.
 *
 * `value` is deliberately `unknown`. The editable vocabulary includes `multi`
 * (an array) alongside string/number/boolean/null, and each kind's `write` leg
 * validates and coerces its own key. Narrowing here would REJECT values the
 * server action accepts today — a behavior change dressed up as a type.
 *
 * The WORKSPACE is absent from this body and must never be added to it. It is
 * resolved from the session by `resolveTenant`, exactly as the server action
 * resolves it from `activeWorkspaceId()`: a request body can no more name its
 * own tenant than a tool call can (`excellent/server/authz.ts` header).
 */
export const objectWriteFieldBody = z.object({
  key: z.string().min(1),
  value: z.unknown(),
  /**
   * The one line a gated option (`ObjectFieldOption.noteLabel`) collected before
   * it let the write go. It rides STRAIGHT to the kind's seam as the fourth
   * argument (a goal appends it to its timeline, an application stores a
   * rejection reason) and is touched by nothing else — a write ARGUMENT, never a
   * value, and never a second write path.
   */
  note: z.string().optional(),
});
export type ObjectWriteFieldBody = z.infer<typeof objectWriteFieldBody>;

/**
 * Mirrors `WriteObjectFieldResult` (`excellent/src/modes/object-write-types.ts`)
 * exactly, and the mount is what keeps the two from drifting: the handler
 * returns the impl's result directly, so a divergence is a compile error at the
 * `mount()` call rather than a runtime response-validation failure.
 *
 * A DOMAIN REFUSAL IS A 200 WITH `ok:false`, NOT AN HTTP ERROR — deliberately.
 * Two reasons, both from the peek PRD's §Failure surfacing: refusals render
 * INLINE in the row (`PeekField.status`), never as a toast and never as a thrown
 * boundary; and over `apiCall` a thrown `ApiError` flattens the discriminant
 * into prose (`packages/ui/src/api/client.ts:62-64`), which would destroy the
 * `noop` case altogether. `noop` is a NON-ERROR NON-SUCCESS (an idempotent
 * transition — `already_enrolled`, advance `no-change`, an idempotent stage
 * move): it must render neutral and must NOT apply the value or take a new
 * baseline, which an HTTP status code cannot say.
 */
export const objectWriteFieldResponse = z.union([
  z.object({
    ok: z.literal(true),
    status: z.enum(["ok", "noop"]),
    /** The FRESHLY RE-READ object, so the client reconciles against server truth. */
    object: objectPeekSchema,
    message: z.string().optional(),
  }),
  z.object({
    ok: z.literal(false),
    status: z.literal("error"),
    message: z.string(),
  }),
]);
export type ObjectWriteFieldResponse = z.infer<typeof objectWriteFieldResponse>;

/** Apply ONE field write to the object at `uid`, dispatched by its own kind. */
export const objectsWriteField: Operation<
  { uid: string },
  void,
  ObjectWriteFieldBody,
  ObjectWriteFieldResponse
> = {
  method: "POST",
  path: ({ uid }) => `/api/objects/${uid}/field`,
  params: z.object({ uid: z.string().min(1) }),
  body: objectWriteFieldBody,
  response: objectWriteFieldResponse,
};

export const objects = {
  get: objectsGet,
  writeField: objectsWriteField,
};
